// eslint-disable-next-line simple-import-sort/imports
import { useCallback, useEffect } from "react";
import { DragDropContext, Droppable } from "react-beautiful-dnd";
import { useTranslation } from "react-i18next";
import { Box, Button, Center, Paper, Text, Title, useMantineTheme } from "@mantine/core";
import { IconPlus } from "@tabler/icons-react";

import "./styles.css";

import { QuestionType } from "src/graphql/generated/graphql";

import { useQuestionnaireFormContext } from "../formContext";
import { QuestionsOverLimit } from "./overLimit";
import { Question } from "./question";
import { SUPPORT_EMAIL } from "src/utils/constants";

export interface QuestionsEditorFormProps {
    hideTitle?: boolean;
    customQuestionKeyPrefix?: string;
    questionsLimit: number;
}

export const QuestionsEditorForm = ({
    hideTitle,
    customQuestionKeyPrefix,
    questionsLimit,
}: QuestionsEditorFormProps) => {
    const { t } = useTranslation(["common"]);

    const theme = useMantineTheme();

    const questionnaireForm = useQuestionnaireFormContext();

    const handleAddQuestion = useCallback(() => {
        if ((questionnaireForm.values?.questions || []).length >= questionsLimit) {
            return;
        }

        questionnaireForm.insertListItem("questions", {
            key: `${customQuestionKeyPrefix}::${Math.random().toString(36).substring(2)}`,
            type: QuestionType.Radio,
            isOptional: false,
            text: "",
            options: [],
            condition: null,
        });
    }, [customQuestionKeyPrefix, questionnaireForm, questionsLimit]);

    const handleOnDragEnd = useCallback(
        ({ source, destination, type }) => {
            if (!destination) {
                return;
            }

            if (type === "question") {
                questionnaireForm.reorderListItem("questions", {
                    from: source.index,
                    to: destination.index,
                });
            }

            if (type.startsWith("option")) {
                const questionIndex = type.split("-")[1];
                questionnaireForm.reorderListItem(`questions.${questionIndex}.options`, {
                    from: source.index,
                    to: destination.index,
                });
            }
        },
        [questionnaireForm]
    );

    useEffect(() => {
        if (!questionnaireForm.values?.questions?.length) {
            handleAddQuestion();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return (
        <>
            {!hideTitle && (
                <>
                    <Title order={2} mb="4" mt="8">
                        {t("questionnaire_questions")}
                    </Title>
                    <Paper p="lg" radius="md" mx="-lg" mt="lg">
                        <Text component="p" mt={0} mb="sm">
                            {t("questionnaire_questions_description", { questionsLimit })}
                        </Text>
                        <Title order={4} mb="2">
                            {t("questionnaire_questions_want_more")}
                        </Title>
                        <Text component="p" mt="xs" mb={0}>
                            {t("questionnaire_questions_get_more")}
                        </Text>
                        <Button component="a" variant="outline" size="sm" mt="md" href={`mailto:${SUPPORT_EMAIL}`}>
                            {t("questionnaire_questions_want_survey")}
                        </Button>
                    </Paper>
                </>
            )}
            <Box mx="-lg" mt="lg">
                <DragDropContext onDragEnd={handleOnDragEnd}>
                    <Droppable droppableId="questions" direction="vertical" type="question">
                        {(provided) => (
                            <Box {...provided.droppableProps} ref={provided.innerRef}>
                                {(questionnaireForm.values?.questions || []).map((question, index) => (
                                    <Question key={question?.key} index={index} />
                                ))}
                                {provided.placeholder}
                            </Box>
                        )}
                    </Droppable>
                </DragDropContext>
            </Box>
            {(questionnaireForm.values?.questions || []).length < questionsLimit ? (
                <Box
                    mx="-lg"
                    mb="-lg"
                    pt="lg"
                    pb="lg"
                    style={{
                        cursor: "pointer",
                        borderBottomLeftRadius: theme.radius.sm,
                        borderBottomRightRadius: theme.radius.sm,
                    }}
                    sx={{
                        "&:hover": {
                            backgroundColor: theme.colorScheme === "dark" ? theme.colors.dark[9] : theme.colors.gray[0],
                        },
                    }}
                    onClick={handleAddQuestion}
                >
                    <Center>
                        <IconPlus /> {t("questionnaire_question_add")}
                    </Center>
                </Box>
            ) : (
                <Box mx="-lg" mb="-lg" pt="lg" pb="lg">
                    <Center>
                        <Text color="dimmed">
                            {t("questionnaire_questions_limit_reached", { limit: questionsLimit })}
                        </Text>
                    </Center>
                    <QuestionsOverLimit questionsLimit={questionsLimit} />
                </Box>
            )}
        </>
    );
};
